// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for retrieving and filtering customer lists from the Hardfin system.
 * Provides type-safe customer list retrieval with Zod validation and server-side filtering.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { CrmSyncStatus } from '@/constants/crm-sync-status';
import { Sort } from '@/constants/sort';
import { customerSchema } from '@/customer/api/types';
import queryString from 'query-string';

type CustomerSortProperty =
  | 'name'
  | 'locations'
  | 'assets-owned'
  | 'assets-on-location';

/**
 * Number of companies the companies table shows per page.
 */
export const CUSTOMER_PAGE_SIZE = 50;

/**
 * Fewest characters a name search needs before the server filters by it.
 */
const MINIMUM_CUSTOMER_SEARCH_LENGTH = 3;

/**
 * Schema for validating the response from the get customers API endpoint.
 * The endpoint answers one page of customers and how many match across every page.
 */
const customersResponseSchema = z
  .object({
    data: z
      .object({
        results: z.array(customerSchema),
        totalCount: z.number(),
      })
      .nullable(),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    customers: data?.results ?? [],
    totalCount: data?.totalCount ?? 0,
  }));

/**
 * Type definition for the get customers API response.
 * Contains one page of customers, the total match count, and request metadata.
 */
type CustomersResponse = z.infer<typeof customersResponseSchema>;

/**
 * Filter options for customer list retrieval.
 * @property isArchived - Filter by archive status: true for archived only, false for active only, 'all' for both
 * @property query - Text matched against customer names on the server
 * @property isCustomer - Filter to only companies with customer role
 * @property isSupplier - Filter to only companies with supplier role
 */
export interface CustomerFilter {
  isArchived?: boolean | 'all';
  query?: string;
  isCustomer?: boolean;
  isSupplier?: boolean;
  syncStatuses?: CrmSyncStatus[];
  // Pagination / sort — only sent by the listing table
  page?: number;
  pageSize?: number;
  sort?: Sort<CustomerSortProperty>;
}

/**
 * Maps a table sort property to the column the server sorts by.
 */
function toSortBy(
  sort: Sort<CustomerSortProperty> | undefined
): string | undefined {
  if (!sort) {
    return undefined;
  }

  const map: Record<CustomerSortProperty, string> = {
    name: 'name',
    locations: 'location_count',
    'assets-owned': 'asset_count',
    'assets-on-location': 'assets_at_location',
  };
  return map[sort.property];
}

/**
 * Checks whether a search is long enough for the server to filter by it.
 */
export function isSearchableCustomerQuery(query: string | undefined) {
  return (query?.trim().length ?? 0) >= MINIMUM_CUSTOMER_SEARCH_LENGTH;
}

/**
 * Fetches customers from the API with optional server-side filtering, search,
 * sorting, and pagination.
 *
 * Callers that omit page/pageSize receive all customers (backend default
 * page size is 5000).
 */
export async function getCustomers(
  filter?: CustomerFilter
): Promise<CustomersResponse> {
  const url = '/v1/customer';
  const response = await api.get(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      archived: filter?.isArchived,
      isCustomer: filter?.isCustomer,
      isSupplier: filter?.isSupplier,
      syncStatuses: filter?.syncStatuses,
      search: isSearchableCustomerQuery(filter?.query)
        ? filter?.query?.trim()
        : undefined,
      page: filter?.page,
      limit: filter?.pageSize,
      sortBy: toSortBy(filter?.sort),
      sortOrder: filter?.sort?.direction,
    },
  });

  return parseResponse(customersResponseSchema, response.data, {
    schemaName: 'Customers',
    endpoint: url,
  });
}
